import React, { useState } from 'react';
import { useQuery } from '@common/hooks';
import { OFFER_CONFIRMATION_QUERY } from '@applicant-flow/graphql/queries';
import {
  ButtonGroup,
  CommentBlock,
  CommentHeader,
  Drawer,
  ErrorHandler,
  LoadingState,
  ShowComments,
  Text,
  Link,
} from '@common/components';
import { buildDialogUrl } from '@common/services/dialogs-service';
import { Button } from 'antd';
import { AcceptOfferAction } from 'works/components/accept-offer-action';
import { OfferChangeRequestDrawer } from 'works/components/offer-change-request-drawer';
import {
  ChangeRequestDialogBox,
  RetractChangeRequest,
  OfferInfo,
} from '@applicant-flow/components';
import { CommentInfoFragmentType } from '@common/graphql';

export const OfferConfirmationDrawer = ({
  open,
  onClose,
  offerId,
}: {
  open: boolean;
  onClose: () => void;
  offerId?: string;
}) => {
  const { data, error } = useQuery(OFFER_CONFIRMATION_QUERY, {
    variables: { id: offerId || '' },
    skip: !offerId,
  });

  const offerActions = [];
  const [showChangeRequestDrawer, setShowChangeRequestDrawer] = useState(false);
  const [
    hasChangeRequestDrawerReturnedSuccess,
    setHasChangeRequestDrawerReturnedSuccess,
  ] = useState(false);

  if (error) {
    return (
      <ErrorHandler
        error={error}
        log="OfferConfirmationDrawer"
        messageType="contextual"
      />
    );
  }

  const offer = data?.offer;

  const hasPendingChangeRequested = offer?.state === 'change_requested';

  const offerRejectionDrawerUrl = buildDialogUrl('offer-rejection-drawer', {
    props: {
      offerId: offer?.id || '',
    },
  });

  if (offer?.canReject?.value) {
    offerActions.push(
      <Link to={offerRejectionDrawerUrl}>
        <Button danger key="rejectionButton" size="large" type="primary">
          Reject
        </Button>
      </Link>
    );
  }

  if (offer?.state === 'offered') {
    offerActions.push(
      <Button
        key="changeRequestButton"
        size="large"
        onClick={() => setShowChangeRequestDrawer(true)}
      >
        Request Changes
      </Button>
    );

    offerActions.push(
      <AcceptOfferAction
        key="accept"
        offerId={offer?.id}
        disabled={!offer?.canAccept?.value}
      >
        Accept Offer
      </AcceptOfferAction>
    );
  }

  if (offer?.state === 'offered' && !offer?.canAccept?.value) {
    offerActions.push(
      <Text key="unavailable" color="coral">
        This position is no longer available.
      </Text>
    );
  }

  const showActionButtons =
    !!offerActions.length &&
    !hasPendingChangeRequested &&
    !hasChangeRequestDrawerReturnedSuccess;

  const changeRequest =
    offer?.offerChangeRequests?.length &&
    offer?.offerChangeRequests[offer?.offerChangeRequests?.length - 1];

  return (
    <>
      <Drawer
        title="Offer Confirmation"
        className="offer-confirmation-drawer"
        open={open}
        onClose={onClose}
        footer={showActionButtons && <ButtonGroup>{offerActions}</ButtonGroup>}
      >
        {offer ? (
          <OfferInfo
            application={offer?.staffingRequestApplication}
            offer={offer}
            headerSlot={
              changeRequest && (
                <ChangeRequestDialogBox changeRequest={changeRequest}>
                  {hasPendingChangeRequested && (
                    <RetractChangeRequest changeRequestId={changeRequest.id} />
                  )}
                </ChangeRequestDialogBox>
              )
            }
            footerSlot={
              offer?.comments?.length ? (
                <ShowComments
                  showMode="all"
                  commentable={offer}
                  headerSlot={<CommentHeader text="Offer Comments" />}
                  render={(comment: CommentInfoFragmentType) => (
                    <CommentBlock key={comment.id} comment={comment} />
                  )}
                />
              ) : null
            }
          />
        ) : (
          <LoadingState />
        )}
      </Drawer>

      <OfferChangeRequestDrawer
        onDrawerClose={() => setShowChangeRequestDrawer(false)}
        onSuccess={() => setHasChangeRequestDrawerReturnedSuccess(true)}
        visible={showChangeRequestDrawer}
        application={offer?.staffingRequestApplication}
        offer={offer}
        type="offer_acceptance"
      />
    </>
  );
};
